import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";

// ── Zephyr brand colours ──────────────────────────────────────────────────────
const ZEPHYR_BLUE    = "#003d82";
const ZEPHYR_BLUE_LT = "#e8f0fd";
const ZEPHYR_ACCENT  = "#0066cc";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
}

function fmtAUD(value) {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(Number(value ?? 0));
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <Box sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid #e5e7eb",
      backgroundColor: "#f9fafb",
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
    }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: ZEPHYR_BLUE, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: "text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

// ── Table styles ──────────────────────────────────────────────────────────────
const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#fff",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: `2px solid ${ZEPHYR_BLUE}`,
  backgroundColor: ZEPHYR_BLUE,
  py: 0.75,
  px: 1.25,
};

const bodyCellSx = {
  fontSize: "0.82rem",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
  py: 0.6,
  px: 1.25,
};

const numCellSx = { ...bodyCellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// ── Main component ────────────────────────────────────────────────────────────
const ChemistZephyrTab = ({ chemistUUID }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(API_PATHS.CHEMIST_ZEPHYR, "GET", null, { ChemistUUID: chemistUUID });
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load Zephyr data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <CircularProgress size={28} sx={{ color: ZEPHYR_BLUE }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const scriptsPerDay  = data.ScriptsPerDay  ?? [];
  const recentScripts  = data.RecentScripts  ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label="Scripts (30d)"
          value={Number(data.TotalScripts30d ?? 0).toLocaleString()}
          sub={`~${data.AvgScriptsPerDay ?? 0}/day avg`}
        />
        <StatCard
          label="Batches (30d)"
          value={Number(data.TotalBatches30d ?? 0).toLocaleString()}
        />
        <StatCard
          label="Value (30d)"
          value={fmtAUD(data.Value30d)}
          sub="Invoiced"
        />
        <StatCard
          label="All-time received"
          value={fmtAUD(data.AllTimeValue)}
        />
      </Box>

      {/* Chart (left) + Recent scripts table (right) — 50/50 */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2, alignItems: "start" }}>

        {/* Bar chart */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ZEPHYR_BLUE }}>
              Scripts per day (30d)
            </Typography>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={fetchData} disabled={loading} sx={{ color: ZEPHYR_BLUE }}>
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={scriptsPerDay} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={fmtDate}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <ReTooltip
                  formatter={(v) => [v, "Scripts"]}
                  labelFormatter={(v) => `Date: ${fmtDate(v)}`}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                />
                <Bar dataKey="count" fill={ZEPHYR_ACCENT} radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Recent scripts table */}
        {recentScripts.length > 0 ? (
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ZEPHYR_BLUE, mb: 1.5 }}>
              Recent scripts (30d)
            </Typography>
            <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Item</TableCell>
                    <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>Invoiced</TableCell>
                    <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>Received</TableCell>
                    <TableCell sx={{ ...headerCellSx, textAlign: "right" }}>Batch date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentScripts.map((row) => (
                    <TableRow
                      key={row.ScriptID}
                      sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover td": { backgroundColor: ZEPHYR_BLUE_LT } }}
                    >
                      <TableCell sx={bodyCellSx}>{row.txtItem || "—"}</TableCell>
                      <TableCell sx={{ ...numCellSx, color: ZEPHYR_BLUE, fontWeight: 700 }}>
                        {row.InvAmount != null ? `$${Number(row.InvAmount).toFixed(2)}` : "—"}
                      </TableCell>
                      <TableCell sx={{ ...numCellSx, color: Number(row.RcvdAmount) > 0 ? "#16a34a" : "text.secondary", fontWeight: 700 }}>
                        {row.RcvdAmount != null ? `$${Number(row.RcvdAmount).toFixed(2)}` : "—"}
                      </TableCell>
                      <TableCell sx={{ ...numCellSx, color: "text.secondary" }}>
                        {fmtDate(row.ScrBatchDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ color: "text.disabled", fontSize: "0.85rem" }}>No scripts in the last 30 days.</Typography>
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default ChemistZephyrTab;
